import {
	Box,
	Money,
	Button,
	Stack,
	Inline,
	Table,
	TableHeaderDataCell,
	TableDataCell,
	Icons,
	Heading,
	Grid,
	Col,
	Text,
	AsyncSelect,
	Divider,
	Component,
	useDragDrop,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import {
	TMoney,
	addMoney,
	createMoney,
	formatMoneyByIntl,
	multipleMoneyBy,
	subtractMoney,
} from "@sembark-travel/money"
import {
	DatePickerField,
	EmptyNumberValidator,
	FieldArray,
	Form,
	FormSpy,
	GetFieldValue,
	PercentageInputField,
	SelectField,
	SelectInputField,
	SubmissionError,
	SwitchInputField,
	TextAreaInputField,
	TextInputField,
	arrayMutators,
	isTruthy,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useMemo, useEffect, useRef, useState } from "react"
import { Optional, Required } from "utility-types"
import {
	formatMoneyByDecimal,
	getMoneyRatio,
	moneyParseByDecimal,
} from "@sembark-travel/money"
import { dateToUTCString, endOf } from "@sembark-travel/datetime-utils"
import config from "../config"
import * as Validator from "yup"
import {
	AddressBillingDetails,
	AddressContactDetails,
	AddressLocation,
	IAddress,
} from "../Addresses"
import { ILocation, SelectLocations } from "../Locations"
import { TInvoice, invoicesXHR } from "./store"
import { ButtonLink } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import { SelectBankAccounts, T_BankAccount } from "../BankAccounts"
import { TTenantBrand } from "../Brands"
import {
	ITermsAndCondition,
	SelectTermsAndConditions,
} from "../TermsAndConditions"
import { normalizeNumber } from "@sembark-travel/number-utils"
import { SelectTaxType, TTaxType } from "../TaxTypes"
import { Dialog } from "@sembark-travel/ui-dialog"

export type InvoiceFormData = {
	currency: string
	overview?: string
	due_at?: Date
	issued_at?: Date
	total_amount?: number
	expected_total_amount?: number
	reference_id?: string
	notes?: string
	terms_and_conditions_id?: ITermsAndCondition
	terms_and_conditions?: ITermsAndCondition
	items?: Array<{
		id?: number
		currency: string
		quantity: number
		description?: string
		hsn?: string
		rate_per_quantity?: number
		total_taxable_amount?: number
		tax_applied_on_base_amount: 0 | 1
		//Flag to control each item taxes calculation either percentage or absoulte amount (Avaialable On the header of taxes table).
		//Our InvoiceForm maintains "tax_applied_on_percentage" flag at root of each item as single source of truth for all taxes in a single item, as this setting is global for all taxes inside an item
		//which can be toggle from the tax table header inside an item.
		tax_applied_on_percentage: 0 | 1
		taxes?: Array<{
			currency: string
			editing_amount: 0 | 1
			tax_type?: {
				id: number
				name: string
				display_name: string
			}
			name?: string
			short_name?: string
			percentage?: number
			amount?: number
		}>
	}>
	seller_name: string
	seller_address?: IAddress
	seller_functional_currency: string
	buyer_name: string
	buyer_address?: IAddress
	location_of_supply?: ILocation
	confirmed?: 0 | 1
	bank_account?: T_BankAccount
	tenant_brand: TTenantBrand
}

export type TTemplateProformaInvoiceData = {
	default_hsn: null | string
	display_hsn: boolean
	display_place_of_supply: boolean
	invoice_title: string
}

/**
 * Preview component showing how user input will be rounded/ceiled
 * Shows the actual value that will be stored after applying the rounding strategy
 */
function DisplayMoneyWithCeilPreview({
	value,
	currency,
	rounding = "ceil",
}: {
	value: string | number
	currency: string
	rounding?: "round" | "ceil" | "floor"
}) {
	const numValue = Number(value)
	if (isNaN(numValue) || numValue === 0) return null

	const parsedMoney = moneyParseByDecimal(numValue, currency, rounding)
	const displayValue = Number(formatMoneyByDecimal(parsedMoney))

	// Only show preview if the value differs from input
	if (displayValue === numValue) return null

	return (
		<Box paddingX="4">
			<Text color="success" fontSize="sm">
				=<Money money={parsedMoney} />
			</Text>
		</Box>
	)
}

/**
 * Configuration object representing how filters affect form behavior
 */
export type TInvoiceFormBehavior = {
	hideTaxBreakup: boolean
}

/**
 * Calculate tax base amount based on item configuration
 * @returns The tax base (either total base amount or total taxable amount)
 */
function calculateTaxBase(params: {
	rate_per_quantity: number | undefined
	quantity: number
	total_taxable_amount: number | undefined
	tax_applied_on_base_amount: 0 | 1 | undefined
	currency: string
}): TMoney {
	const {
		rate_per_quantity,
		quantity,
		total_taxable_amount,
		tax_applied_on_base_amount,
		currency,
	} = params

	// Calculate total base amount (rate × quantity)
	const baseAmountPerQuantity = moneyParseByDecimal(
		Number(rate_per_quantity || 0),
		currency,
		"ceil"
	)
	const totalBaseAmount = multipleMoneyBy(baseAmountPerQuantity, quantity || 1)

	// Calculate total taxable amount
	const totalTaxableAmountMoney = moneyParseByDecimal(
		Number(total_taxable_amount || 0),
		currency,
		"ceil"
	)

	// Return tax base based on flag
	return tax_applied_on_base_amount === 1
		? totalBaseAmount
		: totalTaxableAmountMoney
}

/**
 * Calculate tax percentage from amount and tax base
 */
function calculateTaxPercentageFromAmount(
	taxAmount: TMoney,
	taxBase: TMoney
): number {
	//If taxAmount or taxBase is negative or 0, then percentage = 0
	if (taxAmount.amount <= 0 || taxBase.amount <= 0) {
		return 0
	}

	const percentage = getMoneyRatio(taxAmount, taxBase, 0) * 100

	return percentage
}

/**
 * Calculate tax amount from percentage using ceiling rounding
 */
function calculateTaxAmountFromPercentage(
	taxBase: TMoney,
	percentage: number
): TMoney {
	//Returning tax as 0 if taxBase in negative or 0

	if (taxBase.amount <= 0) {
		return createMoney(0, taxBase.currency)
	}

	/**
	 * Calculate tax amount from percentage with ceiling rounding
	 *
	 * Example: 5% of ₹1290.47 = 64.5235 → ₹64.53 (rounded up)
	 */
	function calculateTaxAmountWithCeil(
		taxBase: TMoney,
		percentage: number,
		currency: string
	): TMoney {
		const baseDecimal = Number(formatMoneyByDecimal(taxBase) || 0)
		const taxDecimal = (baseDecimal * percentage) / 100
		return moneyParseByDecimal(taxDecimal, currency, "ceil")
	}
	return calculateTaxAmountWithCeil(taxBase, percentage, taxBase.currency)
}

/**
 *
 * Tax calculation util function that handles both amount→percentage and percentage→amount
 */
function calculateTaxValues(
	taxBase: TMoney,
	input: { percentage?: number; amount?: TMoney }
): { percentage: number; amount: TMoney } {
	if (input.percentage != null) {
		// Calculate amount from percentage
		const amount = calculateTaxAmountFromPercentage(taxBase, input.percentage)
		return {
			percentage: input.percentage,
			amount,
		}
	} else if (input.amount != null) {
		// Calculate percentage from amount
		const percentage = calculateTaxPercentageFromAmount(input.amount, taxBase)
		return {
			percentage,
			amount: input.amount,
		}
	}

	const zeroAmount = moneyParseByDecimal(0, taxBase.currency)
	return { percentage: 0, amount: zeroAmount }
}

/**
 * Automatically recalculates tax amounts/percentages when the tax base changes due to:
 * - Toggle of "Tax Applied on Base Amount" checkbox
 * - Changes in base amount, quantity, or taxable amount
 */
function TaxRecalculatorPerItemPerTaxSync({
	itemIndex,
	itemFieldName,
	taxIndex,
	taxFieldName,
}: {
	itemIndex: number
	itemFieldName: string // "items[0]"
	taxIndex: number
	taxFieldName: string // "items[0].taxes[0]"
}) {
	const form = useForm()

	const { value: currency } = useFieldValue<string>("currency")

	const { value: quantity } = useFieldValue<number>(`${itemFieldName}.quantity`)

	const { value: taxAppliedOnBase } = useFieldValue<0 | 1 | undefined>(
		`${itemFieldName}.tax_applied_on_base_amount`
	)
	const { value: baseAmountPerQuantity } = useFieldValue<number | undefined>(
		`${itemFieldName}.rate_per_quantity`
	)

	const { value: totalTaxableAmount } = useFieldValue<number | undefined>(
		`${itemFieldName}.total_taxable_amount`
	)
	const { value: taxAppliedOnPercentage } = useFieldValue<0 | 1 | undefined>(
		`${itemFieldName}.tax_applied_on_percentage`
	)

	//Tax Base either - baseAmountPerQuantity or taxableAmountPerQuantity
	const taxBase = useMemo(() => {
		return calculateTaxBase({
			rate_per_quantity: baseAmountPerQuantity,
			quantity,
			total_taxable_amount: totalTaxableAmount,
			tax_applied_on_base_amount: taxAppliedOnBase,
			currency,
		})
	}, [
		taxAppliedOnBase,
		baseAmountPerQuantity,
		totalTaxableAmount,
		currency,
		quantity,
	])

	const taxBaseRef = useRef<TMoney | null>(null)

	useEffect(() => {
		taxBaseRef.current = taxBase

		const h = setTimeout(() => {
			const formState = form.getState()
			const currentValues = formState.values as InvoiceFormData

			const currentTax = currentValues.items?.[itemIndex]?.taxes?.[taxIndex]

			if (!currentTax) return

			const currentTaxPercentage = currentTax.percentage
			const currentTaxAmount = currentTax.amount

			let newValues: { percentage: number; amount: TMoney } | null = null

			if (
				taxAppliedOnPercentage === 1 &&
				currentTaxPercentage != null &&
				currentTaxPercentage >= 0
			) {
				// Percentage mode: recalculating amount from percentage
				newValues = calculateTaxValues(taxBase, {
					percentage: currentTaxPercentage,
				})

				const calculatedTaxAmount = Number(
					formatMoneyByDecimal(newValues.amount) || 0
				)

				if (currentTaxAmount !== calculatedTaxAmount) {
					form.change(`${taxFieldName}.amount`, calculatedTaxAmount)
				}
			} else if (
				taxAppliedOnPercentage === 0 &&
				currentTaxAmount != null &&
				currentTaxAmount > 0
			) {
				// Amount mode: recalculating percentage from amount
				const amountMoney = moneyParseByDecimal(
					currentTaxAmount,
					currency,
					"ceil"
				)
				newValues = calculateTaxValues(taxBase, { amount: amountMoney })

				if (Number(currentTaxPercentage || 0) !== newValues.percentage) {
					form.change(`${taxFieldName}.percentage`, newValues.percentage)
				}
			}
		}, 100)

		return () => {
			clearTimeout(h)
		}
	}, [
		taxBase,
		taxAppliedOnPercentage,
		currency,
		taxFieldName,
		form,
		itemIndex,
		taxIndex,
	])

	return null
}

export function InvoiceForm<T extends TInvoice = TInvoice>({
	onSuccess,
	onCancel,
	initialValues: propInitialValues,
	sellerFunctionalCurrency,
	currency,
	onSubmit,
	onEditBuyerAddress,
	templateInvoiceData,
	hideTaxBreakup,
}: {
	onSuccess: (invoice: T) => void
	onCancel: () => void
	sellerFunctionalCurrency: string
	currency: string
	initialValues?: Optional<InvoiceFormData>
	templateInvoiceData?: TTemplateProformaInvoiceData
	onSubmit: (
		payload: Omit<
			InvoiceFormData,
			| "due_at"
			| "seller_name"
			| "seller_address"
			| "seller_functional_currency"
			| "buyer_name"
			| "buyer_address"
			| "location_of_supply"
			| "bank_account"
			| "terms_and_conditions_id"
		> & {
			due_at: string | null
			timezone_offset: number
			location_of_supply: string | number | null
			bank_account?: number
			terms_and_conditions_id: number | null
			hide_tax_breakup?: 0 | 1
		}
	) => Promise<T>
	onEditBuyerAddress?: () => Promise<{ address?: IAddress }>
	hideTaxBreakup?: 0 | 1
}) {
	// Determine form behavior based on both dynamic api filters and custom filters like - hideTaxBreakup
	const formBehavior = useMemo(
		() => ({
			hideTaxBreakup: Boolean(hideTaxBreakup),
		}),
		[hideTaxBreakup]
	)

	const [initialValues, setInitialValues] = useState<Optional<InvoiceFormData>>(
		() => {
			return {
				...propInitialValues,
				seller_functional_currency: sellerFunctionalCurrency,
				currency,
				confirmed: 0,
				bank_account: propInitialValues?.bank_account,
				terms_and_conditions_id: propInitialValues?.terms_and_conditions,
				items: (propInitialValues?.items || []).map((item) => ({
					...item,
					taxes: item.taxes?.map((tax) => ({
						...tax,
						tax_type: {
							id: tax.short_name,
							name: tax.short_name,
							display_name: tax.short_name,
						},
					})),
				})),
			}
		}
	)
	const tenantBrand = propInitialValues?.tenant_brand

	//Conditional validation schema based on applied filters which alters the form behavior
	const validationSchema = useMemo(() => {
		const itemShape = formBehavior.hideTaxBreakup
			? {
					description: Validator.string().required(
						"Please provide description of the item"
					),
					hsn: EmptyNumberValidator().nullable(),
					quantity: EmptyNumberValidator()
						.required("Please provide quantity")
						.positive("Quantity must be positive"),
					rate_per_quantity: EmptyNumberValidator().required(
						"Please provide the base price of the item"
					),
					// When hideTaxBreakup is true, tax-related fields are optionals
					total_taxable_amount: EmptyNumberValidator().nullable(),
					tax_applied_on_base_amount: Validator.mixed().nullable(),
					taxes: Validator.array().nullable(),
				}
			: {
					description: Validator.string().required(
						"Please provide description of the item"
					),
					hsn: EmptyNumberValidator().nullable(),
					quantity: EmptyNumberValidator()
						.required("Please provide quantity")
						.positive("Quantity must be positive"),
					rate_per_quantity: EmptyNumberValidator().required(
						"Please provide the base price of the item"
					),
					total_taxable_amount: Validator.number().when(
						"tax_applied_on_base_amount",
						{
							is: (tax: number) => tax === 0,
							then: (_schema) =>
								EmptyNumberValidator().required(
									"Taxable amount per quantity is required when tax is not applied on base amount only"
								),
							otherwise: (_schema) => EmptyNumberValidator().nullable(),
						}
					),
					tax_applied_on_base_amount: Validator.mixed().nullable(),
					taxes: Validator.array()
						.nullable()
						.of(
							Validator.object().shape({
								tax_type: Validator.object().shape({
									display_name: Validator.string().required("Required"),
								}),
								percentage: EmptyNumberValidator().nullable(),
								amount: EmptyNumberValidator().nullable(),
							})
						),
				}

		return Validator.object().shape({
			overview: Validator.string().nullable(),
			due_at: Validator.mixed().required("Please select a due date"),
			bank_account: Validator.mixed().nullable(),
			items: Validator.array()
				.required("Please provide at least one item")
				.min(1, "Please provide at least one item")
				.of(Validator.object().shape(itemShape)),
		})
	}, [formBehavior.hideTaxBreakup])

	return (
		<Form<InvoiceFormData>
			initialValues={initialValues}
			validate={validateFormValues(validationSchema)}
			onSubmit={withServerErrors(
				async ({
					seller_address,
					buyer_address,
					seller_name,
					seller_functional_currency,
					buyer_name,
					location_of_supply,
					confirmed,
					bank_account,
					terms_and_conditions_id,
					items,
					...values
				}) => {
					if (!isTruthy(confirmed)) {
						throw new Error("Please accept the details correctness.")
					}

					const transformItems = items?.map((item) => {
						const { rate_per_quantity, quantity, taxes, ...rest } = item
						const isTaxAppliedOnBaseAmount = item.tax_applied_on_base_amount

						const normalizedTaxes = taxes?.map((tax) => ({
							...tax,
							percentage:
								tax.percentage !== undefined
									? normalizeNumber(tax.percentage, 6)
									: tax.percentage,
							name: tax.tax_type?.display_name,
							short_name: tax.tax_type?.display_name,
						}))

						const commonPayload = {
							taxes: normalizedTaxes,
							rate_per_quantity: Number(
								formatMoneyByDecimal(
									moneyParseByDecimal(
										Number(rate_per_quantity || 0),
										currency,
										"ceil"
									)
								) || 0
							),
							quantity,
						}

						if (isTaxAppliedOnBaseAmount) {
							return {
								...rest,
								...commonPayload,
								//If tax is applied on Base Amount then rate_per_quantity * quanity = total_base_amount is equal to total_taxable_amount
								total_taxable_amount: Number(
									formatMoneyByDecimal(
										multipleMoneyBy(
											moneyParseByDecimal(
												Number(rate_per_quantity || 0),
												currency,
												"ceil"
											),
											quantity
										)
									) || 0
								),
							}
						} else {
							return {
								...rest,
								...commonPayload,
							}
						}
					})

					const payload = {
						...values,
						items: transformItems,
						due_at: values.due_at
							? dateToUTCString(endOf(values.due_at, "day"))
							: null,
						timezone_offset: config.timezoneOffset,
						location_of_supply: location_of_supply
							? location_of_supply.id || location_of_supply.name
							: null,
						bank_account: bank_account?.id,
						terms_and_conditions_id: terms_and_conditions_id?.id || null,
						hide_tax_breakup:
							hideTaxBreakup !== undefined ? hideTaxBreakup : (0 as const),
					}
					const invoice = await onSubmit(payload)
					onSuccess(invoice)
				}
			)}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Stack gap="4">
						<Stack gap="1">
							<Heading fontSize={"lg"} as="h3" textAlign={"center"}>
								{templateInvoiceData?.invoice_title}
							</Heading>
							<Stack
								gap="4"
								borderWidth="1"
								padding="4"
								rounded="lg"
								bgColor="subtle"
							>
								<Grid gap="6">
									<Col sm={12} md>
										<Stack gap="1" maxWidth="sm">
											<Inline gap="4">
												<Heading fontSize="md">
													Seller Billing/Address Details
												</Heading>
												<ButtonLink
													to={
														generatePath("/org/profile") +
														"#address" +
														initialValues.seller_address?.id
													}
													level="tertiary"
													size="sm"
												>
													<Icons.Pencil />
												</ButtonLink>
											</Inline>
											<Text>Name: {initialValues?.seller_name}</Text>
											{initialValues?.seller_address ? (
												<Inline gap="4" flexWrap="wrap">
													<Stack gap="2">
														<AddressLocation
															address={initialValues.seller_address}
														/>
														<AddressContactDetails
															address={initialValues.seller_address}
														/>
													</Stack>
													<AddressBillingDetails
														hideName
														address={initialValues.seller_address}
													/>
												</Inline>
											) : (
												<Text>N/A</Text>
											)}
										</Stack>
									</Col>
									<Col sm={12} md>
										<Stack gap="1" maxWidth="sm">
											<Inline gap="4">
												<Heading fontSize="md">
													Buyer Billing/Address Details
												</Heading>
												{onEditBuyerAddress ? (
													<Button
														onClick={() =>
															onEditBuyerAddress().then(({ address }) => {
																if (address) {
																	setInitialValues({
																		...initialValues,
																		buyer_name:
																			address.addressing_name ||
																			initialValues.buyer_name,
																		buyer_address:
																			address || initialValues.buyer_address,
																	})
																}
															})
														}
														size="sm"
														level="tertiary"
													>
														<Icons.Pencil />
													</Button>
												) : null}
											</Inline>
											<Text>Name: {initialValues?.buyer_name}</Text>
											{initialValues?.buyer_address ? (
												<Inline gap="4" flexWrap="wrap">
													<Stack gap="2">
														<AddressLocation
															address={initialValues.buyer_address}
														/>
														<AddressContactDetails
															address={initialValues.buyer_address}
														/>
													</Stack>
													<AddressBillingDetails
														hideName
														address={initialValues.buyer_address}
													/>
												</Inline>
											) : (
												<Text>N/A</Text>
											)}
										</Stack>
									</Col>
								</Grid>
							</Stack>
						</Stack>

						<Stack gap="2">
							<Grid gap="4">
								{templateInvoiceData?.display_place_of_supply ? (
									<Col>
										{sellerFunctionalCurrency === "INR" ? (
											<SelectField
												currency={sellerFunctionalCurrency}
												select={SelectPlaceOfSupply}
												fetchOnMount
												name="location_of_supply"
												label="Place of Supply"
												multiple={false}
												onChange={(value: ILocation | undefined) => {
													form.change("location_of_supply", value || undefined)
													if (currency === "INR") {
														if (initialValues?.seller_address && value) {
															//We are handling Indian Taxation Law of "IGST" & "CGST+SGST", if we are selling in outside states from our registered business state,
															//then the business lands in single taxation law, and only "IGST" is applied on them but if they are selling in their own registered business,
															//then both "CGST+SGST" is applied on them.
															const is_integrated =
																initialValues.seller_address?.location
																	?.state_id !== value.state_id
															if (
																window.confirm(
																	`Do you also want to update tax details (${
																		is_integrated ? "IGST" : "CGST+SGST"
																	}) based on the "Place of Supply"`
																)
															) {
																const formValues = form.getState().values
																const items = (formValues.items || [])?.map(
																	({ taxes, ...item }) => {
																		//If user has disabled taxes are disabled for an item, returning item + with empty taxes array.
																		if (!(taxes || []).length) {
																			return {
																				...item,
																				taxes,
																			}
																		}

																		const percentage = (
																			taxes || []
																		).reduce<number>(
																			(total, { percentage }) =>
																				total + Number(percentage || 0),
																			0
																		)

																		const taxBase = calculateTaxBase({
																			rate_per_quantity: item.rate_per_quantity,
																			quantity: item.quantity,
																			total_taxable_amount:
																				item.total_taxable_amount,
																			tax_applied_on_base_amount:
																				item.tax_applied_on_base_amount,
																			currency,
																		})

																		taxes = []
																		if (is_integrated) {
																			const igstAmount =
																				calculateTaxAmountFromPercentage(
																					taxBase,
																					percentage
																				)
																			taxes.push({
																				percentage,
																				amount: Number(
																					formatMoneyByDecimal(igstAmount) || 0
																				),
																				name: "IGST",
																				short_name: "IGST",
																				currency: currency,
																				editing_amount: 0, // default to percentage mode
																			})
																		} else {
																			const center_tax =
																				Math.round((percentage * 100) / 2) / 100

																			const sgst_tax = percentage - center_tax

																			const cgstAmount =
																				calculateTaxAmountFromPercentage(
																					taxBase,
																					center_tax
																				)
																			const sgstAmount =
																				calculateTaxAmountFromPercentage(
																					taxBase,
																					sgst_tax
																				)

																			taxes.push(
																				{
																					percentage: center_tax,
																					amount: Number(
																						formatMoneyByDecimal(cgstAmount) ||
																							0
																					),
																					name: "CGST",
																					short_name: "CGST",
																					currency: currency,
																					editing_amount: 0, // default to percentage mode
																				},
																				{
																					percentage: sgst_tax,
																					amount: Number(
																						formatMoneyByDecimal(sgstAmount) ||
																							0
																					),
																					name: "SGST",
																					short_name: "SGST",
																					currency: currency,
																					editing_amount: 0, // default to percentage mode
																				}
																			)
																		}
																		return {
																			...item,
																			taxes,
																		}
																	}
																)
																form.change("items", items)
															}
														}
													}
												}}
											/>
										) : (
											<SelectField
												select={SelectLocations}
												name="location_of_supply"
												label="Place of Supply"
												secondaryLabel={"optional"}
												multiple={false}
												creatable
											/>
										)}
									</Col>
								) : null}
								<Col>
									<SelectField
										select={SelectBankAccounts}
										tenant_brands={tenantBrand ? [tenantBrand.id] : undefined}
										name="bank_account"
										label="Bank Account"
										currency={currency}
										secondaryLabel={"optional"}
										fetchOnMount
										menuPlaceholder="Request Admin/Accountant to add a new bank account."
									/>
								</Col>
								<Col md={6}>
									<DatePickerField name="due_at" label="Due Date" />
								</Col>
							</Grid>

							<Grid>
								<Col md={6} paddingX={"0"}>
									<TextAreaInputField
										label={"Overview"}
										name={`overview`}
										placeholder="eg. Darjeeling Tour Package - 27 Oct 2025 - 6N, 7D - 2A, 3 Children (10y, 9y, 8y)"
										rows={4}
									/>
								</Col>
							</Grid>
						</Stack>
						<FieldArray<
							Required<InvoiceFormData>["items"][number]
						> name="items">
							{({ fields }) => (
								<Stack gap="4">
									<Inline justifyContent="between" alignItems="center">
										<Heading fontSize="md">Particulars</Heading>
										<GetFieldValue<InvoiceFormData["items"]> name="items">
											{({ value }) => {
												const itemsWithDescription = value?.length
													? value.filter((item) => item?.description)
													: []
												return itemsWithDescription.length >= 2 ? (
													<Component initialState={false}>
														{({ state, setState }) => (
															<>
																<Button
																	status="primary"
																	onClick={() => setState(true)}
																>
																	Re-arrangement
																</Button>
																<ReorderDailogBox
																	open={state}
																	onCancel={() => setState(false)}
																	editableData={value?.map((val, idx) => {
																		return {
																			...val,
																			id: idx,
																		}
																	})}
																	onSuccess={(data) => {
																		form.change("items", data)
																	}}
																/>
															</>
														)}
													</Component>
												) : null
											}}
										</GetFieldValue>
									</Inline>
									<Table bordered hover responsive>
										<thead>
											<tr>
												<TableHeaderDataCell>#</TableHeaderDataCell>
												<TableHeaderDataCell>Particular</TableHeaderDataCell>
												<TableHeaderDataCell>Qty</TableHeaderDataCell>
												<GetFieldValue<string | undefined> name="currency">
													{({ value: currency }) => (
														<TableHeaderDataCell>
															{formBehavior.hideTaxBreakup
																? "Amount"
																: "Base Amount Per Qty"}{" "}
															({currency})
														</TableHeaderDataCell>
													)}
												</GetFieldValue>
												<TableHeaderDataCell textAlign="right">
													&nbsp;
												</TableHeaderDataCell>
												<TableHeaderDataCell>
													Total ({currency})
												</TableHeaderDataCell>

												<TableHeaderDataCell textAlign="right">
													&nbsp;
												</TableHeaderDataCell>
											</tr>
										</thead>
										<tbody>
											{fields.map((name, index) => (
												<tr key={name}>
													<TableDataCell>{index + 1}</TableDataCell>

													<TableDataCell>
														<Stack gap="4">
															<TextAreaInputField
																name={`${name}.description`}
																placeholder="Details regarding the item"
																rows={index === 0 ? 5 : undefined}
															/>

															{templateInvoiceData?.display_hsn ? (
																<Inline
																	gap="2"
																	alignItems={"center"}
																	justifyContent={"end"}
																>
																	<Text fontWeight="semibold">HSN/SAC</Text>

																	<TextInputField
																		type="text"
																		name={`${name}.hsn`}
																		placeholder="e.g. 998555"
																		style={{ minWidth: "150px" }}
																	/>
																</Inline>
															) : null}
														</Stack>
													</TableDataCell>

													<TableDataCell>
														<TextInputField
															type="number"
															name={`${name}.quantity`}
															placeholder="1"
															style={{ maxWidth: "80px" }}
														/>
													</TableDataCell>
													<TableDataCell>
														<GetFieldValue<0 | 1 | undefined>
															name={`${name}.tax_applied_on_base_amount`}
														>
															{({ value: taxAppliedOnBase }) => {
																const isTaxAppliedOnBaseAmount =
																	taxAppliedOnBase === 1
																return (
																	<Stack gap="2">
																		<Inline gap="4">
																			<TextInputField
																				type="number"
																				name={`${name}.rate_per_quantity`}
																				placeholder="e.g. 0"
																				help={({ value }) => {
																					const baseAmountPerQuantity =
																						value as string

																					return (
																						<DisplayMoneyWithCeilPreview
																							value={baseAmountPerQuantity}
																							currency={currency}
																						/>
																					)
																				}}
																			/>
																			{!formBehavior.hideTaxBreakup ? (
																				<Inline
																					justifyContent={"end"}
																					width={"full"}
																				>
																					<Box
																						style={{
																							maxWidth: "120px",
																						}}
																					>
																						<SwitchInputField
																							name={`${name}.tax_applied_on_base_amount`}
																							label="Apply Tax on Base Amount"
																						/>
																					</Box>
																				</Inline>
																			) : null}
																		</Inline>

																		{!formBehavior.hideTaxBreakup ? (
																			<Box
																				style={{
																					marginTop: !isTaxAppliedOnBaseAmount
																						? "98px"
																						: "24px",
																				}}
																			>
																				<FieldArray<
																					Required<
																						Required<InvoiceFormData>["items"][number]
																					>["taxes"][number]
																				>
																					name={`${name}.taxes`}
																				>
																					{({ fields }) => (
																						<GetFieldValue<number | undefined>
																							name={`${name}.rate_per_quantity`}
																						>
																							{({
																								value: baseAmountPerQuantity,
																							}) =>
																								baseAmountPerQuantity ? (
																									<>
																										{!fields.length ? (
																											<Box
																												display={"flex"}
																												justifyContent={"end"}
																											>
																												<Button
																													onClick={() =>
																														fields.push({
																															currency:
																																currency,
																															short_name: "",
																															name: "",
																															percentage: 0,
																															amount: 0,
																															editing_amount: 0, // default to percentage mode
																														})
																													}
																													size="sm"
																												>
																													Add Tax
																												</Button>
																											</Box>
																										) : (
																											<Table bordered hover>
																												<thead>
																													<tr>
																														<TableHeaderDataCell>
																															Tax Name
																														</TableHeaderDataCell>
																														<TableHeaderDataCell>
																															<SelectInputField
																																name={`${name}.tax_applied_on_percentage`}
																																size="sm"
																																style={{
																																	maxWidth:
																																		"150px",
																																}}
																																onChange={(
																																	e
																																) => {
																																	const value =
																																		e
																																			.currentTarget
																																			.value

																																	if (
																																		Number(
																																			value
																																		)
																																	) {
																																		form.change(
																																			`${name}.tax_applied_on_percentage` as never,
																																			1 as never
																																		)
																																	} else {
																																		form.change(
																																			`${name}.tax_applied_on_percentage` as never,
																																			0 as never
																																		)
																																	}
																																}}
																															>
																																<option
																																	value={1}
																																>
																																	Percentage
																																</option>
																																<option
																																	value={0}
																																>
																																	Amount
																																</option>
																															</SelectInputField>
																														</TableHeaderDataCell>
																														<TableHeaderDataCell textAlign="right">
																															<Button
																																onClick={() =>
																																	fields.push({
																																		currency:
																																			currency,
																																		short_name:
																																			"",
																																		name: "",
																																		percentage: 0,
																																		amount: 0,
																																		editing_amount: 0, // default to percentage mode
																																	})
																																}
																																inline
																															>
																																<Icons.Plus />
																															</Button>
																														</TableHeaderDataCell>
																													</tr>
																												</thead>
																												<GetFieldValue<number>
																													name={`${name}.quantity`}
																												>
																													{({
																														value: quantity,
																													}) => (
																														<GetFieldValue<
																															number | undefined
																														>
																															name={`${name}.total_taxable_amount`}
																														>
																															{({
																																value:
																																	totalTaxableAmount,
																															}) => (
																																<tbody>
																																	{fields.map(
																																		(
																																			taxFieldName,
																																			taxIndex
																																		) => (
																																			<tr
																																				key={
																																					taxFieldName
																																				}
																																			>
																																				<TableDataCell>
																																					<GetFieldValue<
																																						| 0
																																						| 1
																																						| undefined
																																					>
																																						name={`${name}.tax_applied_on_base_amount`}
																																					>
																																						{({
																																							value:
																																								taxAppliedOnBase,
																																						}) => {
																																							const taxBase =
																																								calculateTaxBase(
																																									{
																																										rate_per_quantity:
																																											baseAmountPerQuantity,
																																										quantity,
																																										total_taxable_amount:
																																											totalTaxableAmount,
																																										tax_applied_on_base_amount:
																																											taxAppliedOnBase,
																																										currency,
																																									}
																																								)

																																							return (
																																								<SelectField
																																									fetchOnMount
																																									select={
																																										SelectTaxType
																																									}
																																									creatable
																																									disableNonDisplayTaxTypes
																																									name={`${taxFieldName}.tax_type.display_name`}
																																									placeholder="e.g. GST"
																																									onChange={(
																																										value: TTaxType
																																									) => {
																																										const calculatedTaxAmount =
																																											calculateTaxValues(
																																												taxBase,
																																												{
																																													percentage:
																																														value?.default_percentage_value as never,
																																												}
																																											).amount

																																										form.batch(
																																											() => {
																																												form.change(
																																													`${taxFieldName}.tax_type` as never,
																																													value as never
																																												)

																																												form.change(
																																													`${taxFieldName}.percentage` as never,
																																													value?.default_percentage_value as never
																																												)

																																												form.change(
																																													`${taxFieldName}.amount` as never,
																																													Number(
																																														formatMoneyByDecimal(
																																															calculatedTaxAmount
																																														) ||
																																															0
																																													) as never
																																												)
																																											}
																																										)
																																									}}
																																								/>
																																							)
																																						}}
																																					</GetFieldValue>
																																				</TableDataCell>
																																				<TableDataCell>
																																					<GetFieldValue<
																																						| 0
																																						| 1
																																						| undefined
																																					>
																																						name={`${name}.tax_applied_on_percentage`}
																																					>
																																						{({
																																							value:
																																								taxAppliedOnPercentage,
																																						}) => (
																																							<GetFieldValue<
																																								| 0
																																								| 1
																																								| undefined
																																							>
																																								name={`${name}.tax_applied_on_base_amount`}
																																							>
																																								{({
																																									value:
																																										taxAppliedOnBase,
																																								}) => {
																																									const taxBase =
																																										calculateTaxBase(
																																											{
																																												rate_per_quantity:
																																													baseAmountPerQuantity,
																																												quantity,
																																												total_taxable_amount:
																																													totalTaxableAmount,
																																												tax_applied_on_base_amount:
																																													taxAppliedOnBase,
																																												currency,
																																											}
																																										)

																																									return (
																																										<Stack gap="1">
																																											{taxAppliedOnPercentage ===
																																											1 ? (
																																												<PercentageInputField
																																													type="number"
																																													min="0"
																																													name={`${taxFieldName}.percentage`}
																																													placeholder="10.00"
																																													size="sm"
																																													onChange={(
																																														e
																																													) => {
																																														const value =
																																															e
																																																.currentTarget
																																																.value

																																														form.change(
																																															`${taxFieldName}.percentage` as never,
																																															value as never
																																														)

																																														const percentage =
																																															Number(
																																																value ||
																																																	0
																																															)

																																														const calculatedTaxAmount =
																																															calculateTaxValues(
																																																taxBase,
																																																{
																																																	percentage,
																																																}
																																															).amount

																																														form.change(
																																															`${taxFieldName}.amount` as never,
																																															Number(
																																																formatMoneyByDecimal(
																																																	calculatedTaxAmount
																																																) ||
																																																	0
																																															) as never
																																														)
																																													}}
																																												/>
																																											) : (
																																												<TextInputField
																																													type="number"
																																													min="0"
																																													name={`${taxFieldName}.amount`}
																																													placeholder="0.00"
																																													size="sm"
																																													onChange={(
																																														e
																																													) => {
																																														const value =
																																															e
																																																.currentTarget
																																																.value

																																														form.change(
																																															`${taxFieldName}.amount` as never,
																																															value as never
																																														)

																																														const percentage =
																																															calculateTaxValues(
																																																taxBase,
																																																{
																																																	amount:
																																																		moneyParseByDecimal(
																																																			Number(
																																																				value
																																																			),
																																																			currency,
																																																			"ceil"
																																																		),
																																																}
																																															).percentage
																																														form.change(
																																															`${taxFieldName}.percentage` as never,
																																															percentage as never
																																														)
																																													}}
																																													help={({
																																														value,
																																													}) => {
																																														const amount =
																																															value as string

																																														return (
																																															<DisplayMoneyWithCeilPreview
																																																value={
																																																	amount
																																																}
																																																currency={
																																																	currency
																																																}
																																															/>
																																														)
																																													}}
																																													style={{
																																														maxWidth:
																																															"90px",
																																													}}
																																												/>
																																											)}

																																											{
																																												<GetFieldValue<
																																													| number
																																													| undefined
																																												>
																																													name={`${taxFieldName}.${taxAppliedOnPercentage !== 0 ? "amount" : "percentage"}`}
																																												>
																																													{({
																																														value:
																																															amountOrPercentageValue,
																																													}) => (
																																														<Text
																																															as="div"
																																															color="muted"
																																															fontSize="sm"
																																														>
																																															{taxAppliedOnPercentage ===
																																															1 ? (
																																																<Box>
																																																	<Inline gap="1">
																																																		<Money
																																																			money={moneyParseByDecimal(
																																																				Number(
																																																					amountOrPercentageValue ||
																																																						0
																																																				),
																																																				currency
																																																			)}
																																																		/>
																																																	</Inline>
																																																</Box>
																																															) : null}
																																														</Text>
																																													)}
																																												</GetFieldValue>
																																											}
																																										</Stack>
																																									)
																																								}}
																																							</GetFieldValue>
																																						)}
																																					</GetFieldValue>
																																				</TableDataCell>
																																				<TableDataCell textAlign="right">
																																					<Inline
																																						gap="2"
																																						justifyContent="end"
																																					>
																																						<Button
																																							inline
																																							onClick={() =>
																																								fields.remove(
																																									taxIndex
																																								)
																																							}
																																						>
																																							<Icons.Cancel />
																																						</Button>
																																					</Inline>
																																				</TableDataCell>

																																				{/* tax recalculator with form state sync - per tax row */}
																																				<TaxRecalculatorPerItemPerTaxSync
																																					itemIndex={
																																						index
																																					}
																																					itemFieldName={
																																						name
																																					}
																																					taxIndex={
																																						taxIndex
																																					}
																																					taxFieldName={
																																						taxFieldName
																																					}
																																				/>
																																			</tr>
																																		)
																																	)}
																																</tbody>
																															)}
																														</GetFieldValue>
																													)}
																												</GetFieldValue>
																											</Table>
																										)}
																									</>
																								) : null
																							}
																						</GetFieldValue>
																					)}
																				</FieldArray>
																			</Box>
																		) : null}
																	</Stack>
																)
															}}
														</GetFieldValue>
													</TableDataCell>
													<TableDataCell textAlign="right"></TableDataCell>
													<TableDataCell>
														<GetFieldValue<0 | 1 | undefined>
															name={`${name}.tax_applied_on_base_amount`}
														>
															{({ value: taxAppliedOnBase }) => {
																return (
																	<Stack gap="2">
																		<GetFieldValue<number>
																			name={`${name}.rate_per_quantity`}
																		>
																			{({ value: baseAmountPerQuantity }) => (
																				<GetFieldValue<number>
																					name={`${name}.quantity`}
																				>
																					{({ value: quantity }) => (
																						<Stack gap="1">
																							<Text fontWeight="semibold">
																								Total Base Amount
																							</Text>
																							<Box
																								bgColor="subtle"
																								borderWidth="1"
																								rounded="md"
																								borderColor="emphasis"
																								paddingLeft="4"
																								paddingRight="4"
																								style={{
																									paddingTop: "7px",
																									paddingBottom: "7px",
																								}}
																							>
																								<Money
																									money={multipleMoneyBy(
																										moneyParseByDecimal(
																											Number(
																												baseAmountPerQuantity
																											),
																											currency,
																											"ceil"
																										),
																										quantity
																									)}
																								/>
																							</Box>
																						</Stack>
																					)}
																				</GetFieldValue>
																			)}
																		</GetFieldValue>

																		{taxAppliedOnBase === 0 &&
																		!formBehavior.hideTaxBreakup ? (
																			<TextInputField
																				type="number"
																				label="Total Taxable Amount"
																				name={`${name}.total_taxable_amount`}
																				placeholder="Total Taxable Amount"
																				help={({ value }) => {
																					const taxableAmountPerQuantity =
																						value as string

																					return (
																						<DisplayMoneyWithCeilPreview
																							value={taxableAmountPerQuantity}
																							currency={currency}
																						/>
																					)
																				}}
																			/>
																		) : null}

																		{!formBehavior.hideTaxBreakup ? (
																			<Box>
																				<GetFieldValue<
																					Required<
																						Required<InvoiceFormData>["items"][number]
																					>["taxes"]
																				>
																					name={`${name}.taxes`}
																				>
																					{({ value: itemTaxes }) =>
																						(itemTaxes || []).length ? (
																							<Box>
																								<Box
																									marginTop={"2"}
																									paddingY={"2"}
																								>
																									<Text fontWeight="semibold">
																										Taxes
																									</Text>
																								</Box>
																								<Stack gap="10" marginTop={"4"}>
																									{(itemTaxes || []).map(
																										(tax, i) => (
																											<Box
																												key={`${tax.name}-${i}`}
																												bgColor="subtle"
																												borderWidth="1"
																												rounded="md"
																												borderColor="emphasis"
																												paddingLeft="4"
																												paddingRight="4"
																												style={{
																													paddingTop: "4px",
																													paddingBottom: "4px",
																												}}
																											>
																												<Money
																													money={moneyParseByDecimal(
																														Number(tax.amount),
																														currency,
																														"ceil"
																													)}
																												/>
																											</Box>
																										)
																									)}
																								</Stack>
																							</Box>
																						) : null
																					}
																				</GetFieldValue>
																			</Box>
																		) : null}
																	</Stack>
																)
															}}
														</GetFieldValue>
													</TableDataCell>

													<TableDataCell
														textAlign="right"
														style={{
															height: "100%",
															position: "relative",
															minWidth: "120px",
														}}
													>
														<Stack
															justifyContent="between"
															paddingX={"2"}
															paddingY={"2"}
															style={{
																height: "100%",
																position: "absolute",
																top: 0,
																left: 0,
																right: 0,
																bottom: 0,
															}}
														>
															<Inline gap="2" justifyContent="end">
																<Button
																	size="sm"
																	onClick={() => fields.remove(index)}
																>
																	<Icons.Cancel />
																</Button>
															</Inline>

															{/* Per-item total calculation */}
															<GetFieldValue<number | undefined>
																name={`${name}.rate_per_quantity`}
															>
																{({ value: ratePerQuantity }) => (
																	<GetFieldValue<number>
																		name={`${name}.quantity`}
																	>
																		{({ value: quantity }) => (
																			<GetFieldValue<number | undefined>
																				name={`${name}.total_taxable_amount`}
																			>
																				{({ value: totalTaxableAmount }) => (
																					<GetFieldValue<0 | 1 | undefined>
																						name={`${name}.tax_applied_on_base_amount`}
																					>
																						{({ value: taxAppliedOnBase }) => (
																							<GetFieldValue<0 | 1 | undefined>
																								name={`${name}.tax_applied_on_percentage`}
																							>
																								{({
																									value: taxAppliedOnPercentage,
																								}) => (
																									<GetFieldValue<
																										Required<
																											Required<InvoiceFormData>["items"][number]
																										>["taxes"]
																									>
																										name={`${name}.taxes`}
																									>
																										{({ value: taxes }) => {
																											// Calculate tax base and total base amount
																											const taxBase =
																												calculateTaxBase({
																													rate_per_quantity:
																														ratePerQuantity,
																													quantity:
																														quantity || 1,
																													total_taxable_amount:
																														totalTaxableAmount,
																													tax_applied_on_base_amount:
																														taxAppliedOnBase,
																													currency,
																												})

																											const itemBaseAmountPerQuantity =
																												moneyParseByDecimal(
																													Number(
																														ratePerQuantity
																													),
																													currency,
																													"ceil"
																												)
																											const totalBaseAmount =
																												multipleMoneyBy(
																													itemBaseAmountPerQuantity,
																													quantity || 1
																												)

																											// If hideTaxBreakup, total is just base amount
																											if (
																												formBehavior.hideTaxBreakup
																											) {
																												return (
																													<Box
																														paddingY="1"
																														paddingX="2"
																														rounded="md"
																													>
																														<Text
																															fontWeight="semibold"
																															fontSize="lg"
																														>
																															<Money
																																money={
																																	totalBaseAmount
																																}
																															/>
																														</Text>
																													</Box>
																												)
																											}

																											// Calculate with taxes
																											const totalTaxableAmountMoney =
																												moneyParseByDecimal(
																													Number(
																														totalTaxableAmount
																													),
																													currency,
																													"ceil"
																												)

																											const itemTotalTax = (
																												taxes || []
																											).reduce<TMoney>(
																												(
																													taxAmount,
																													{ percentage, amount }
																												) => {
																													if (
																														taxAppliedOnPercentage ===
																														1
																													) {
																														return addMoney(
																															taxAmount,
																															calculateTaxValues(
																																taxBase,
																																{
																																	percentage:
																																		Number(
																																			percentage ||
																																				0
																																		),
																																}
																															).amount
																														)
																													} else {
																														return addMoney(
																															taxAmount,
																															moneyParseByDecimal(
																																Number(amount),
																																currency,
																																"ceil"
																															)
																														)
																													}
																												},
																												createMoney(0, currency)
																											)

																											const itemTotal =
																												taxAppliedOnBase === 1
																													? addMoney(
																															totalBaseAmount,
																															itemTotalTax
																														)
																													: addMoney(
																															totalBaseAmount,
																															totalTaxableAmountMoney,
																															itemTotalTax
																														)

																											return (
																												<Box
																													paddingY="1"
																													paddingX="1"
																													rounded="md"
																												>
																													<Text
																														fontWeight="semibold"
																														fontSize="lg"
																													>
																														<Money
																															money={itemTotal}
																														/>
																													</Text>
																												</Box>
																											)
																										}}
																									</GetFieldValue>
																								)}
																							</GetFieldValue>
																						)}
																					</GetFieldValue>
																				)}
																			</GetFieldValue>
																		)}
																	</GetFieldValue>
																)}
															</GetFieldValue>
														</Stack>
													</TableDataCell>
												</tr>
											))}
										</tbody>
									</Table>

									<Inline justifyContent={"end"}>
										<Button
											onClick={async () => {
												const data = {
													currency: currency,
													quantity: 1,
													rate_per_quantity: 0,
													total_taxable_amount: 0,
													tax_applied_on_base_amount: 1 as const,
													tax_applied_on_percentage: 1 as const, // default to percentage mode
													description: "",
													hsn: templateInvoiceData?.default_hsn || "",
													taxes: !formBehavior.hideTaxBreakup
														? [
																{
																	name: "",
																	percentage: 0,
																	amount: 0,
																	short_name: "",
																	editing_amount: 0 as const, // default to percentage mode
																	currency: currency,
																},
															]
														: undefined,
												}
												fields.push(data)
											}}
											size="sm"
											status="primary"
										>
											<Icons.Plus /> Add Item
										</Button>
									</Inline>
								</Stack>
							)}
						</FieldArray>

						<FormSpy<InvoiceFormData> subscription={{ values: true }}>
							{({ values, form }) => {
								const { currency, items, expected_total_amount } = values
								const total = (items || []).reduce<TMoney>(
									(total, item) => {
										const quantity = item.quantity || 1

										// Calculate tax base
										const taxBase = calculateTaxBase({
											rate_per_quantity: item.rate_per_quantity,
											quantity,
											total_taxable_amount: item.total_taxable_amount,
											tax_applied_on_base_amount:
												item.tax_applied_on_base_amount,
											currency,
										})

										const itemBaseAmountPerQuantity = moneyParseByDecimal(
											Number(item.rate_per_quantity),
											currency,
											"ceil"
										)

										const totalBaseAmount = multipleMoneyBy(
											itemBaseAmountPerQuantity,
											quantity
										) // Base Amount × Quantity = Total Base Amount of an Item

										// Total calculation when tax breakup is hidden
										if (formBehavior.hideTaxBreakup) {
											return addMoney(total, totalBaseAmount)
										}

										// Total calculation when tax breakup is not hidden
										const totalTaxableAmount = moneyParseByDecimal(
											Number(item.total_taxable_amount),
											currency,
											"ceil"
										)

										const itemTotalTax = (item.taxes || []).reduce<TMoney>(
											(taxAmount, { percentage, amount }) => {
												if (item.tax_applied_on_percentage === 1) {
													// Using percentage calculation (tax_applied_on_percentage: 1 = percentage mode)
													return addMoney(
														taxAmount,
														calculateTaxValues(taxBase, {
															percentage: Number(percentage || 0),
														}).amount
													)
												} else {
													// Use amount calculation (tax_applied_on_percentage: 0 = amount mode)
													return addMoney(
														taxAmount,
														moneyParseByDecimal(
															Number(amount),
															currency,
															"ceil"
														)
													)
												}
											},
											createMoney(0, currency)
										)

										// Item total depends on tax_applied_on_base_amount flag

										const itemTotal =
											item.tax_applied_on_base_amount === 1
												? addMoney(totalBaseAmount, itemTotalTax) // No taxable amount in total
												: addMoney(
														totalBaseAmount,
														totalTaxableAmount,
														itemTotalTax
													) // Including taxable amount along with base amount in total

										return addMoney(total, itemTotal)
									},
									createMoney(0, currency)
								)

								const isCalculatedTaxEqualsToExpectedAmount =
									Number(formatMoneyByDecimal(total)) ===
									Number(expected_total_amount || 0)

								return (
									<Inline
										padding="4"
										bgColor={
											!isCalculatedTaxEqualsToExpectedAmount
												? "warning"
												: "primary"
										}
										gap="2"
										rounded="lg"
										justifyContent={"end"}
									>
										<Stack gap="1" textAlign={"right"}>
											<Text fontSize="lg" fontWeight="semibold">
												Total:{" "}
												<Money money={total} showCurrency keepTrailingZero />
											</Text>
											{total.amount &&
											!isCalculatedTaxEqualsToExpectedAmount ? (
												<Box>
													<Button
														size="sm"
														onClick={() =>
															form.change(
																"items",
																(values.items || []).concat([
																	{
																		description: "Round Off",
																		currency,
																		quantity: 1,
																		rate_per_quantity: Number(
																			formatMoneyByDecimal(
																				subtractMoney(
																					moneyParseByDecimal(
																						Number(expected_total_amount || 0),
																						currency
																					),
																					total
																				)
																			)
																		),
																		tax_applied_on_base_amount: 1,
																		tax_applied_on_percentage: 1,
																	},
																])
															)
														}
													>
														Round Off
													</Button>
												</Box>
											) : null}

											<Box color="warning">
												{!isCalculatedTaxEqualsToExpectedAmount ? (
													<Inline gap="1">
														<Box>
															<Icons.Attention size={"4"} />
														</Box>
														<Text fontWeight="semibold">
															The invoice amount (
															{formatMoneyByIntl(total, {
																showCurrency: true,
															})}
															) is not equal to the package amount (
															{formatMoneyByIntl(
																moneyParseByDecimal(
																	Number(expected_total_amount || 0),
																	currency
																),
																{
																	showCurrency: true,
																}
															)}
															)
														</Text>
													</Inline>
												) : null}
											</Box>
										</Stack>
									</Inline>
								)
							}}
						</FormSpy>
						<Divider marginY="0" />
						<Grid gap="4">
							<Col sm={6} md>
								<TextAreaInputField
									label="Special Notes"
									secondaryLabel="optional"
									name="notes"
									placeholder="Any special notes here"
								/>
							</Col>
							<Col sm={6} md>
								<SelectField
									name="terms_and_conditions_id"
									label="Terms and Conditions"
									select={SelectTermsAndConditions}
									fetchOnMount={true}
									featuredIn="invoices"
								/>
							</Col>
						</Grid>
						<Divider sm />
						<GetFieldValue<0 | 1 | boolean> name="confirmed">
							{({ value }) => {
								const selected = isTruthy(value)
								return (
									<Stack
										padding="4"
										bgColor={selected ? "success" : "warning"}
										rounded="lg"
										borderWidth="1"
										gap="4"
									>
										<Text fontWeight="semibold">
											Please cross check all the details and correct it if
											something is not as per rules and regulations.
										</Text>
										<SwitchInputField
											name="confirmed"
											label="I confirm that all the details of this proforma invoice are correct"
										/>
									</Stack>
								)
							}}
						</GetFieldValue>
						<Divider marginY="0" />
						<SubmissionError />
						<Inline gap="4">
							<Button disabled={submitting} type="submit">
								{submitting ? "Please wait..." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={() => onCancel()}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function SelectPlaceOfSupply({
	currency,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	currency: string
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			cacheKey={`/invoices/locations-of-supply/${currency}`}
			fetch={(q) =>
				invoicesXHR(xhr)
					.getLocationsOfSupply({ q, currency })
					.then((resp) => resp)
			}
		/>
	)
}

function ReorderDailogBox({
	onCancel,
	open,
	editableData,
	onSuccess,
}: {
	open: boolean
	onCancel: () => void
	editableData: InvoiceFormData["items"]
	onSuccess: (data: InvoiceFormData["items"]) => void
}) {
	return (
		<Dialog
			lg
			open={open}
			onClose={onCancel}
			title={
				<Stack gap="2">
					<Heading fontSize="lg">Rearrange Particulars Items</Heading>
					<Text fontSize="sm" fontWeight="normal" color="muted">
						Drag and drop to rearrange the order of particular items. This will
						only affect the display order.
					</Text>
				</Stack>
			}
		>
			<Dialog.Body>
				<ParticularsReorderTable
					refresh={(reorderedItems) => {
						onSuccess(reorderedItems)
					}}
					data={editableData}
					onCancel={onCancel}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

function ParticularsReorderTable({
	data,
	refresh,
	onCancel,
}: {
	data: InvoiceFormData["items"]
	refresh: (reorderedItems: InvoiceFormData["items"]) => void
	onCancel: () => void
}) {
	const [particulars, setParticulars] = useState<InvoiceFormData["items"]>(data)

	const {
		handleDragStart,
		handleDragOver,
		handleDrop,
		getRowStyle,
		handleDragLeave,
	} = useDragDrop(particulars || [], (newOrderedData) => {
		setParticulars(newOrderedData)
	})

	function handleSubmit() {
		refresh(particulars)
		onCancel()
	}

	return (
		<Stack>
			<Table
				bordered
				responsive
				headers={[
					"Description",
					`Amount (${(particulars || [])[0]?.currency || ""})`,
				]}
				children={
					<tbody>
						{particulars?.map((particular, r) => {
							return particular.description ? (
								<Box
									style={{
										...getRowStyle(r),
										cursor: "grab",
									}}
									as="tr"
									key={particular.id}
									draggable={true}
									onDragStart={(e: React.DragEvent<HTMLTableRowElement>) =>
										handleDragStart(e, r)
									}
									onDrop={(e: React.DragEvent<HTMLTableRowElement>) =>
										handleDrop(e, r)
									}
									onDragOver={(e: React.DragEvent<HTMLTableRowElement>) =>
										handleDragOver(e, r)
									}
									onDragLeave={handleDragLeave}
								>
									<TableDataCell>
										<Inline gap="3" alignItems="center">
											<Box
												padding="1"
												bgColor="accent"
												color="primary"
												borderWidth="1"
												borderColor="accent"
												rounded="md"
											>
												<Icons.SwitchHorizontal rotate="90" />
											</Box>

											{particular.description}
										</Inline>
									</TableDataCell>
									<TableDataCell>
										{(particular.rate_per_quantity || 0) * particular.quantity}
									</TableDataCell>
								</Box>
							) : null
						})}
					</tbody>
				}
			/>
			<Box marginTop="4" textAlign="right">
				<Button level="primary" onClick={handleSubmit}>
					Apply Order
				</Button>
			</Box>
		</Stack>
	)
}
